import {
  createContext,
  useCallback,
  useContext,
  useMemo,
  useState,
} from 'react'
import Box from '@mui/material/Box'
import Tooltip from '@mui/material/Tooltip'

// When something happened, and on whose clock (owner, 2026-11-04). A stream
// time reads on the staff member's own clock; a click on any one switches
// every time in the app to the account's clock, when it happened for them,
// and another click switches back. One switch for the whole app, kept in this
// browser, so the Moments and Users pages read alike.
//
// The account's clock is its `timezone` as the server names it: the zone its
// phone last reported, UTC when it has none the server can read. A person who
// travelled reads in where they are now. Every time carries its zone's
// abbreviation, so which clock is showing never has to be remembered.

const KEY = 'reclip-admin-clock'

const FORMATS = {
  datetime: {
    month: 'short',
    day: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  },
  // Inside one day: a minute.
  minute: { hour: 'numeric', minute: '2-digit' },
  // Inside one span: candidates seconds apart stay apart.
  second: { hour: 'numeric', minute: '2-digit', second: '2-digit' },
}

const ClockContext = createContext({ theirs: false, toggle: () => {} })

const remembered = () => {
  try {
    return window.localStorage.getItem(KEY) === 'theirs'
  } catch {
    return false
  }
}

export const ClockProvider = ({ children }) => {
  const [theirs, setTheirs] = useState(remembered)
  const toggle = useCallback(
    () =>
      setTheirs((was) => {
        try {
          window.localStorage.setItem(KEY, was ? 'mine' : 'theirs')
        } catch {
          // the switch still holds for this visit
        }
        return !was
      }),
    []
  )
  const value = useMemo(() => ({ theirs, toggle }), [theirs, toggle])
  return <ClockContext.Provider value={value}>{children}</ClockContext.Provider>
}

export const useClock = () => useContext(ClockContext)

// An instant as text on the clock the switch names: the account's zone when
// it is on theirs and the zone is known, the viewer's otherwise. A zone this
// browser does not know reads on the viewer's clock rather than failing.
export const clockText = (ms, zone, theirs, format = 'datetime') => {
  const options = { ...FORMATS[format], timeZoneName: 'short' }
  try {
    return new Intl.DateTimeFormat('en-US', {
      ...options,
      ...(theirs && zone ? { timeZone: zone } : {}),
    }).format(new Date(ms))
  } catch {
    return new Intl.DateTimeFormat('en-US', options).format(new Date(ms))
  }
}

// The text form for a tooltip or a sentence that cannot hold a stamp.
export const useClockText = () => {
  const { theirs } = useClock()
  return useCallback(
    (ms, zone, format) => clockText(ms, zone, theirs, format),
    [theirs]
  )
}

// An instant that switches the clock when clicked. Its click stays its own:
// it never opens the row or the fold it sits in.
export const Stamp = ({ ms, zone, format = 'datetime' }) => {
  const { theirs, toggle } = useClock()
  if (ms == null) return null
  const onTheirs = theirs && Boolean(zone)
  const flip = (event) => {
    event.stopPropagation()
    toggle()
  }
  return (
    <Tooltip
      placement="top"
      disableInteractive
      title={
        !zone
          ? `Your clock: the account's timezone is unknown`
          : onTheirs
          ? `Their clock (${zone}). Click for yours`
          : `Your clock. Click for theirs (${zone})`
      }
    >
      <Box
        component="span"
        role="button"
        tabIndex={0}
        data-row-action
        onClick={flip}
        onKeyDown={(event) => {
          if (event.key === 'Enter' || event.key === ' ') {
            event.preventDefault()
            flip(event)
          }
        }}
        sx={{
          cursor: 'pointer',
          whiteSpace: 'nowrap',
          textDecoration: 'underline dotted',
          textUnderlineOffset: '2px',
          color: onTheirs ? 'primary.main' : 'inherit',
        }}
      >
        {clockText(ms, zone, theirs, format)}
      </Box>
    </Tooltip>
  )
}
